import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fileUrl } from "@/lib/api";
import { Download, MapPin, Phone, Mail, Calendar, FileText, Receipt, History as HistoryIcon } from "lucide-react";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const OrderDetailModal = ({ order, onClose, actions }) => {
  if (!order) return null;

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>{order.order_id}</span>
            <span className={`pav-chip status-${order.status}`}>{order.status?.replace(/_/g, " ")}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Customer */}
          <section data-testid="modal-customer-section">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#06B6D4] mb-2">Customer</h3>
            <div className="bg-[#F4F6FB] rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <p className="font-semibold">{order.customer_name}</p>
              <p className="text-neutral-600 flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" /> {order.customer_phone || "—"}
              </p>
              <p className="text-neutral-600 flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {order.customer_email || "—"}
              </p>
              {order.customer_address && <p className="text-neutral-600 sm:col-span-2">{order.customer_address}</p>}
              {order.tin_number && <p className="text-neutral-600 text-xs">TIN: {order.tin_number}</p>}
            </div>
          </section>

          {/* Services / line items */}
          <section data-testid="modal-services-section">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#06B6D4] mb-2">Services & specifications</h3>
            <div className="space-y-2">
              {(order.items || []).map((it, idx) => (
                <div key={idx} className="border border-[#E2E8F0] rounded-lg p-3 text-sm" data-testid={`modal-item-${idx}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold">{it.service_name || it.category}</p>
                      <p className="text-neutral-500 text-xs mt-0.5">
                        {it.width}×{it.height} {it.unit} · {(it.finishing || "—").replace(/_/g, " ")} · ×{it.quantity}
                      </p>
                      {it.notes && <p className="text-xs text-neutral-500 italic mt-1">Note: {it.notes}</p>}
                    </div>
                    <p className="font-bold text-[#0B1E3F]">{peso(it.line_total)}</p>
                  </div>
                  {it.file_url && (
                    <div className="mt-2 flex items-center gap-3">
                      <img src={fileUrl(it.file_url)} alt="" className="h-16 w-16 rounded object-cover border border-[#E2E8F0]"
                        onError={(e) => { e.target.style.display = "none"; }} />
                      <a href={fileUrl(it.file_url)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#0B1E3F] hover:underline">
                        <Download className="h-3.5 w-3.5" /> Download design file
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Fulfillment */}
          <section data-testid="modal-fulfillment-section">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#06B6D4] mb-2">Fulfillment</h3>
            <div className="bg-[#F4F6FB] rounded-lg p-4 text-sm">
              <p className="font-semibold capitalize">{order.fulfillment_type}</p>
              {order.fulfillment_type === "pickup" ? (
                <p className="text-neutral-600 mt-1 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Pickup at: {order.pickup_datetime ? new Date(order.pickup_datetime).toLocaleString() : "Anytime"}
                </p>
              ) : (
                <div className="text-neutral-600 mt-1 space-y-1">
                  <p>{order.within_metro_naga ? "Within Metro Naga" : `Outside Metro Naga (${order.distance_km}km)`}</p>
                  {order.google_map_link && (
                    <a href={order.google_map_link} target="_blank" rel="noreferrer"
                      className="text-[#0B1E3F] hover:underline inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" /> Open in Google Maps
                    </a>
                  )}
                  <p className="text-xs">Shipping cost: {peso(order.shipping_cost)}</p>
                </div>
              )}
            </div>
          </section>

          {/* Payment */}
          <section data-testid="modal-payment-section">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#06B6D4] mb-2">Payment</h3>
            <div className="bg-[#F4F6FB] rounded-lg p-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span>Subtotal (net VAT)</span><span>{peso(order.net_vat || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>VAT (12%)</span><span>{peso(order.vat_amount || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span><span>{peso(order.shipping_cost || 0)}</span>
              </div>
              <div className="flex justify-between font-display font-extrabold text-lg text-[#0B1E3F] pt-2 border-t border-[#E2E8F0] mt-2">
                <span>TOTAL</span><span>{peso(order.total)}</span>
              </div>
              <div className="pt-2 mt-2 border-t border-[#E2E8F0] flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="text-neutral-600">
                  Mode: <span className="capitalize font-semibold">{(order.payment_mode || "pending").replace("_", " ")}</span>
                </span>
                <span className={`pav-chip ${order.payment_status === "unpaid" ? "status-cancelled" : order.payment_status === "partial" ? "status-pending_payment" : "status-completed"}`}>
                  {order.payment_status || (order.status === "pending_payment" ? "pending" : "paid")}
                </span>
              </div>
              {order.payment?.transaction_id && (
                <p className="text-xs text-neutral-500 mt-1">
                  <Receipt className="h-3 w-3 inline" /> Txn: {order.payment.transaction_id}
                  {order.payment.confirmed_by && ` by ${order.payment.confirmed_by}`}
                </p>
              )}
            </div>
          </section>

          {/* Audit log / history */}
          <section data-testid="modal-history-section">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#06B6D4] mb-2 flex items-center gap-1.5">
              <HistoryIcon className="h-3.5 w-3.5" /> Audit log
            </h3>
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#F4F6FB] text-neutral-600 uppercase tracking-wider text-[10px]">
                  <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Note</th></tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  <tr data-testid="history-created">
                    <td className="px-3 py-2 text-neutral-600">{new Date(order.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2"><span className="pav-chip status-pending_payment">created</span></td>
                    <td className="px-3 py-2 text-neutral-600">{order.created_by || order.customer_email || "system"}</td>
                    <td className="px-3 py-2 text-neutral-500">Order created</td>
                  </tr>
                  {(order.history || []).map((h, i) => (
                    <tr key={i} data-testid={`history-row-${i}`}>
                      <td className="px-3 py-2 text-neutral-600">{new Date(h.at).toLocaleString()}</td>
                      <td className="px-3 py-2"><span className={`pav-chip status-${h.status}`}>{h.status?.replace(/_/g, " ")}</span></td>
                      <td className="px-3 py-2 text-neutral-600">{h.by || "—"}</td>
                      <td className="px-3 py-2 text-neutral-500">{h.note || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {order.notes && (
            <section data-testid="modal-notes-section">
              <h3 className="text-xs uppercase tracking-wider font-semibold text-[#06B6D4] mb-2 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> Notes
              </h3>
              <p className="text-sm text-neutral-700 bg-[#F4F6FB] rounded-lg p-3 italic">{order.notes}</p>
            </section>
          )}

          {actions && <div className="pt-3 flex flex-wrap gap-2 border-t border-[#E2E8F0]">{actions}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailModal;
